import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-6">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您要查看的文章不存在或已被删除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm cursor-pointer hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: article.categoryName, path: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '美女视频推荐',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://pozino.cn/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://pozino.cn/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://pozino.cn/category/${article.categoryId}`, name: article.categoryName } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://pozino.cn/article/${article.id}`, name: article.title } },
    ],
  };

  const formatReadCount = (count: number) => {
    if (count > 10000) return `${(count / 10000).toFixed(1)}万`;
    return count.toString();
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <div className="w-full px-4 md:px-6 py-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    <span>{formatReadCount(article.readCount)} 阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    <span>{article.date}</span>
                  </div>
                </div>
              </header>

              {/* Featured Image */}
              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none mb-10 article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 py-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}